(() => {
  'use strict';

  const CONTAINER_ID = 'section-register-form';
  const ENDPOINT = '/apps/company-register';
  const POLL_INTERVAL = 500;
  const MAX_POLLS = 60; // 30s max wait

  // ── Logging ──────────────────────────────────────────────────────────
  const LOG_PREFIX = '[B2B-Register]';

  const log  = (...args) => console.log(LOG_PREFIX, ...args);
  const warn = (...args) => console.warn(LOG_PREFIX, ...args);
  const err  = (...args) => console.error(LOG_PREFIX, ...args);

  // ── i18n ─────────────────────────────────────────────────────────────
  const I18N = {
    en: {
      submitting:       'Submitting...',
      requiredFields:   'Please fill in the required fields: company name, VAT number, email.',
      requiredAddress:  'Please fill in the required billing address fields: address, city, postal code.',
      addressLead:      'Please fill in the required address fields:',
      fieldAddress:     'address',
      fieldCity:        'city',
      fieldZip:         'postal code',
      inProgress:       'Registration in progress...',
      success:          'Registration completed successfully! Your B2B account will be activated.',
      registrationError:'Registration error',
      genericError:     'An error occurred. Please try again.',
    },
    pl: {
      submitting:       'Wysyłanie...',
      requiredFields:   'Proszę wypełnić wymagane pola: nazwa firmy, NIP, e-mail.',
      requiredAddress:  'Proszę wypełnić wymagane pola adresu rozliczeniowego: adres, miasto, kod pocztowy.',
      addressLead:      'Proszę wypełnić wymagane pola adresu:',
      fieldAddress:     'adres',
      fieldCity:        'miasto',
      fieldZip:         'kod pocztowy',
      inProgress:       'Rejestracja w toku...',
      success:          'Rejestracja zakończona pomyślnie! Twoje konto B2B zostanie aktywowane.',
      registrationError:'Błąd rejestracji',
      genericError:     'Wystąpił błąd. Spróbuj ponownie.',
    },
    de: {
      submitting:       'Wird gesendet...',
      requiredFields:   'Bitte füllen Sie die Pflichtfelder aus: Firmenname, USt-IdNr., E-Mail.',
      requiredAddress:  'Bitte füllen Sie die Pflichtfelder der Rechnungsadresse aus: Adresse, Stadt, PLZ.',
      addressLead:      'Bitte füllen Sie die erforderlichen Adressfelder aus:',
      fieldAddress:     'Adresse',
      fieldCity:        'Stadt',
      fieldZip:         'PLZ',
      inProgress:       'Registrierung läuft...',
      success:          'Registrierung erfolgreich abgeschlossen! Ihr B2B-Konto wird aktiviert.',
      registrationError:'Registrierungsfehler',
      genericError:     'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
    },
    fr: {
      submitting:       'Envoi en cours...',
      requiredFields:   'Veuillez remplir les champs obligatoires : nom de l\'entreprise, numéro de TVA, e-mail.',
      requiredAddress:  'Veuillez remplir les champs obligatoires de l\'adresse de facturation : adresse, ville, code postal.',
      addressLead:      'Veuillez remplir les champs d\'adresse obligatoires :',
      fieldAddress:     'adresse',
      fieldCity:        'ville',
      fieldZip:         'code postal',
      inProgress:       'Inscription en cours...',
      success:          'Inscription terminée avec succès ! Votre compte B2B sera activé.',
      registrationError:'Erreur d\'inscription',
      genericError:     'Une erreur est survenue. Veuillez réessayer.',
    },
    it: {
      submitting:       'Invio in corso...',
      requiredFields:   'Compila i campi obbligatori: nome azienda, partita IVA, e-mail.',
      requiredAddress:  'Compila i campi obbligatori dell\'indirizzo di fatturazione: indirizzo, città, CAP.',
      addressLead:      'Compila i campi obbligatori dell\'indirizzo:',
      fieldAddress:     'indirizzo',
      fieldCity:        'città',
      fieldZip:         'CAP',
      inProgress:       'Registrazione in corso...',
      success:          'Registrazione completata con successo! Il tuo account B2B sarà attivato.',
      registrationError:'Errore di registrazione',
      genericError:     'Si è verificato un errore. Riprova.',
    },
    cs: {
      submitting:       'Odesílání...',
      requiredFields:   'Vyplňte prosím povinná pole: název společnosti, DIČ, e-mail.',
      requiredAddress:  'Vyplňte prosím povinná pole fakturační adresy: adresa, město, PSČ.',
      addressLead:      'Vyplňte prosím povinná pole adresy:',
      fieldAddress:     'adresa',
      fieldCity:        'město',
      fieldZip:         'PSČ',
      inProgress:       'Registrace probíhá...',
      success:          'Registrace byla úspěšně dokončena! Váš B2B účet bude aktivován.',
      registrationError:'Chyba registrace',
      genericError:     'Došlo k chybě. Zkuste to prosím znovu.',
    },
    ro: {
      submitting:       'Se trimite...',
      requiredFields:   'Vă rugăm completați câmpurile obligatorii: denumire companie, cod fiscal, e-mail.',
      requiredAddress:  'Vă rugăm completați câmpurile obligatorii ale adresei de facturare: adresă, oraș, cod poștal.',
      addressLead:      'Vă rugăm completați câmpurile obligatorii de adresă:',
      fieldAddress:     'adresă',
      fieldCity:        'oraș',
      fieldZip:         'cod poștal',
      inProgress:       'Înregistrare în curs...',
      success:          'Înregistrare finalizată cu succes! Contul dvs. B2B va fi activat.',
      registrationError:'Eroare de înregistrare',
      genericError:     'A apărut o eroare. Vă rugăm încercați din nou.',
    },
  };

  const LANG_TO_COUNTRY = { pl: 'PL', de: 'DE', fr: 'FR', it: 'IT', cs: 'CZ', ro: 'RO', en: '' };

  function getLang() {
    return (document.documentElement.lang || 'en').toLowerCase().split('-')[0];
  }

  function t(key) {
    const lang = getLang();
    return (I18N[lang] || I18N.en)[key] || I18N.en[key];
  }

  function defaultCountryCode() {
    return LANG_TO_COUNTRY[getLang()] || '';
  }

  // ── State ────────────────────────────────────────────────────────────
  let interceptActive = false;   // true once we've attached listeners to a form
  let submitInProgress = false;  // guard against double-submission

  /**
   * Map Shopify Forms field id / name to our payload.
   *
   * Shopify Forms generates stable, locale-independent id/name attributes
   * (e.g. id="company_name", name="first_name") regardless of the display
   * language. We match on those — no need for translated label strings.
   *
   * Resolution order (see resolveFieldPath):
   *   1. Exact match on input.id   (primary — stable on every market)
   *   2. Exact match on input[name]
   *   3. Partial match on input[name] (skipped for billing_/shipping_address_* keys)
   */
  const FIELD_MAP = {
    // --- Company ---
    'company_name': 'companyName',
    'company': 'companyName',
    'company_tax_id': 'vatNumber',
    'vat': 'vatNumber',
    'nip': 'vatNumber',
    'vat_number': 'vatNumber',

    // --- Personal ---
    'email': 'email',
    'first_name': 'firstName',
    'last_name': 'lastName',
    'phone': 'phone',
    'phone_number': 'phone',

    // --- Billing address ---
    'billing_address_first_name': 'billingAddress.firstName',
    'billing_address_last_name': 'billingAddress.lastName',
    'billing_address_recipient': 'billingAddress.recipient',
    'billing_address_address1': 'billingAddress.address1',
    'billing_address_address2': 'billingAddress.address2',
    'billing_address_zip': 'billingAddress.zip',
    'billing_address_city': 'billingAddress.city',
    'billing_address_phone': 'billingAddress.phone',
    'billing_address_country': 'billingAddress.countryCode',
    'billing_address_country_code': 'billingAddress.countryCode',
    'billing_address_zone': 'billingAddress.zoneCode',

    // --- Shipping address (rendered only when "same as billing" is unchecked) ---
    'shipping_address_first_name': 'shippingAddress.firstName',
    'shipping_address_last_name': 'shippingAddress.lastName',
    'shipping_address_recipient': 'shippingAddress.recipient',
    'shipping_address_address1': 'shippingAddress.address1',
    'shipping_address_address2': 'shippingAddress.address2',
    'shipping_address_zip': 'shippingAddress.zip',
    'shipping_address_city': 'shippingAddress.city',
    'shipping_address_phone': 'shippingAddress.phone',
    'shipping_address_country': 'shippingAddress.countryCode',
    'shipping_address_country_code': 'shippingAddress.countryCode',
    'shipping_address_zone': 'shippingAddress.zoneCode'
  };

  const BILLING_PREFIX = 'billing_address_';
  const SHIPPING_PREFIX = 'shipping_address_';

  // ── Helpers ──────────────────────────────────────────────────────────

  function setNested(obj, path, value) {
    const keys = path.split('.');
    let current = obj;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) current[keys[i]] = {};
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
  }

  function describeInput(input) {
    const parts = [input.tagName.toLowerCase()];
    if (input.type) parts.push(`type=${input.type}`);
    if (input.id) parts.push(`id=${input.id}`);
    const name = input.getAttribute('name');
    if (name) parts.push(`name=${name}`);
    return `<${parts.join(' ')}>`;
  }

  /**
   * Collect all elements matching selector under root, including inside shadow roots.
   */
  function querySelectorAllDeep(root, selector) {
    const out = [];
    if (!root) return out;
    out.push(...root.querySelectorAll(selector));
    for (const el of root.querySelectorAll('*')) {
      if (el.shadowRoot) {
        out.push(...querySelectorAllDeep(el.shadowRoot, selector));
      }
    }
    return out;
  }

  /**
   * Return which DOB part an input represents: 'day' | 'month' | 'year' | null.
   *
   * Shopify Forms DOB fields use stable tokens in id, name, aria-label, and
   * aria-labelledby refs (e.g. "DD", "MM", "YYYY", "day", "month", "year")
   * regardless of locale. We match on those.
   */
  function getDobPart(input) {
    const id = (input.id || '').toLowerCase();
    const name = (input.getAttribute('name') || '').toLowerCase();
    const aria = (input.getAttribute('aria-label') || '').toLowerCase().trim();
    const placeholder = (input.getAttribute('placeholder') || '').toLowerCase().trim();

    // Check aria-labelledby refs (Shopify Forms uses label-DD / label-MM / label-YYYY)
    const root = (input.getRootNode && input.getRootNode()) || document;
    const ariaLabelledby = (input.getAttribute('aria-labelledby') || '').trim();
    let refText = '';
    if (ariaLabelledby) {
      for (const ref of ariaLabelledby.split(/\s+/)) {
        if (!ref) continue;
        const byId = root.querySelector(`#${ref}`);
        if (byId) refText += ` ${byId.textContent.toLowerCase().trim()}`;
      }
      refText = refText.trim();
    }

    const combined = [id, name, aria, placeholder, refText].join(' ');

    if (/\b(dd|day)\b/.test(combined) || /[_-]day\b/.test(combined)) return 'day';
    if (/\b(mm|month)\b/.test(combined) || /[_-]month\b/.test(combined)) return 'month';
    if (/\b(yyyy|year)\b/.test(combined) || /[_-]year\b/.test(combined)) return 'year';
    return null;
  }

  // ── Field resolution ────────────────────────────────────────────────

  function resolveFieldPath(input) {
    const desc = describeInput(input);
    const id = (input.id || '').toLowerCase().trim();
    const name = (input.getAttribute('name') || '').toLowerCase().trim();

    // 1. Exact match on input.id. Shopify Forms uses stable, locale-independent ids
    //    (company_name, billing_address_zip, shipping_address_city, ...) on every
    //    market — verified across FR/IT/DE — so this alone resolves every field.
    if (id && FIELD_MAP[id]) {
      log('Resolve', desc, `-> matched by id "${id}" -> ${FIELD_MAP[id]}`);
      return FIELD_MAP[id];
    }

    // 2. Exact match on input[name]
    if (name && FIELD_MAP[name]) {
      log('Resolve', desc, `-> matched by name "${name}" -> ${FIELD_MAP[name]}`);
      return FIELD_MAP[name];
    }

    // 3. Partial match on name — last-resort fallback for a differently-built form.
    //    Skip prefixed billing/shipping keys, and skip inputs that are themselves a
    //    billing/shipping field, so a generic key like "address" can never collapse a
    //    "shipping_address_zip" field onto shippingAddress.address1 (that exact
    //    substring collision was the bug this replaced).
    const prefixed = id.startsWith(BILLING_PREFIX) || id.startsWith(SHIPPING_PREFIX) ||
                     name.startsWith(BILLING_PREFIX) || name.startsWith(SHIPPING_PREFIX);
    if (name && !prefixed) {
      for (const key of Object.keys(FIELD_MAP)) {
        if (key.startsWith(BILLING_PREFIX) || key.startsWith(SHIPPING_PREFIX)) continue;
        if (name.includes(key)) {
          log('Resolve', desc, `-> matched by name partial "${name}" contains key "${key}" -> ${FIELD_MAP[key]}`);
          return FIELD_MAP[key];
        }
      }
    }

    warn('Resolve', desc, `-> NO MATCH (id="${id}", name="${name}")`);
    return null;
  }

  // ── Payload ─────────────────────────────────────────────────────────

  function buildPayload(form) {
    log('Building payload — scanning form inputs...');

    const payload = {
      companyName: '',
      vatNumber: '',
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      dateOfBirth: '',
      emailConsent: false,
      locale: document.documentElement.lang || '',
      billingSameAsShipping: false,
      shippingAddress: {
        firstName: '',
        lastName: '',
        address1: '',
        address2: '',
        city: '',
        zip: '',
        countryCode: defaultCountryCode(),
        zoneCode: '',
        phone: ''
      },
      billingAddress: {
        firstName: '',
        lastName: '',
        recipient: '',
        address1: '',
        address2: '',
        city: '',
        zip: '',
        countryCode: defaultCountryCode(),
        zoneCode: '',
        phone: ''
      }
    };

    // --- Map text / select inputs via FIELD_MAP ---
    const inputs = form.querySelectorAll('input, select, textarea');
    let mappedCount = 0;
    let skippedCount = 0;
    let unmappedCount = 0;

    log('Found', inputs.length, 'input/select/textarea elements in form');

    for (const input of inputs) {
      const desc = describeInput(input);

      // Skip hidden/submit/button/checkbox (checkboxes handled separately below)
      if (input.type === 'hidden' || input.type === 'submit' ||
          input.type === 'button' || input.type === 'checkbox') {
        log('  Skip', desc, '(type excluded from FIELD_MAP loop)');
        skippedCount++;
        continue;
      }

      const value = input.value || '';
      const path = resolveFieldPath(input);

      if (path && value) {
        log('  Set', path, '=', JSON.stringify(value.length > 60 ? value.substring(0, 60) + '...' : value));
        setNested(payload, path, value);
        mappedCount++;
      } else if (path && !value) {
        warn('  Matched', desc, '->', path, 'but value is EMPTY — skipping');
        unmappedCount++;
      } else {
        unmappedCount++;
      }
    }

    log(`Field mapping summary: mapped=${mappedCount}, skipped=${skippedCount}, unmapped=${unmappedCount}`);

    // --- Newsletter consent checkbox ---
    const emailConsent = form.querySelector('#emailConsent-true, [name="emailConsent-true"]');
    if (emailConsent) {
      payload.emailConsent = emailConsent.checked;
      log(`emailConsent checkbox found — checked=${emailConsent.checked}`);
    } else {
      warn('emailConsent checkbox NOT found (#emailConsent-true / [name="emailConsent-true"])');
    }

    // --- Same-as-billing checkbox ---
    const sameAs = form.querySelector('#same_as_billing_address, [name="same_as_billing_address"]');
    if (sameAs) {
      payload.billingSameAsShipping = sameAs.checked;
      log(`same_as_billing_address checkbox found — checked=${sameAs.checked}`);
    } else {
      warn('same_as_billing_address checkbox NOT found (#same_as_billing_address / [name="same_as_billing_address"])');
    }

    // --- Date of birth (day / month / year: aria-label, placeholder, name, id, label; incl. shadow DOM) ---
    const dobInputs = querySelectorAllDeep(form, 'input');
    let dobDay = null;
    let dobMonth = null;
    let dobYear = null;
    for (const inp of dobInputs) {
      if (inp.type === 'hidden' || inp.type === 'submit' || inp.type === 'button') continue;
      const part = getDobPart(inp);
      if (part === 'day') dobDay = inp;
      else if (part === 'month') dobMonth = inp;
      else if (part === 'year') dobYear = inp;
    }
    log(`DOB fields: day=${dobDay ? `found(val=${dobDay.value})` : 'NOT FOUND'}, month=${dobMonth ? `found(val=${dobMonth.value})` : 'NOT FOUND'}, year=${dobYear ? `found(val=${dobYear.value})` : 'NOT FOUND'}`);

    if (dobDay && dobMonth && dobYear && dobDay.value && dobMonth.value && dobYear.value) {
      payload.dateOfBirth =
        `${dobYear.value.trim()}-${('0' + dobMonth.value.trim()).slice(-2)}-${('0' + dobDay.value.trim()).slice(-2)}`;
      log('dateOfBirth =', payload.dateOfBirth);
    } else {
      warn('dateOfBirth NOT set — one or more DOB fields missing or empty');
    }

    // --- Copy top-level names / phone into shippingAddress if not set ---
    if (!payload.shippingAddress.firstName && payload.firstName) {
      payload.shippingAddress.firstName = payload.firstName;
      log('Copied firstName -> shippingAddress.firstName');
    }
    if (!payload.shippingAddress.lastName && payload.lastName) {
      payload.shippingAddress.lastName = payload.lastName;
      log('Copied lastName -> shippingAddress.lastName');
    }
    if (!payload.shippingAddress.phone && payload.phone) {
      payload.shippingAddress.phone = payload.phone;
      log('Copied phone -> shippingAddress.phone');
    }

    // --- If "same as billing", copy billingAddress -> shippingAddress ---
    if (payload.billingSameAsShipping) {
      log('billingSameAsShipping=true — copying billingAddress -> shippingAddress');
      const { billingAddress: ba, shippingAddress: sa } = payload;
      if (ba.firstName && !sa.firstName) { sa.firstName = ba.firstName;     log('  billing->shipping: firstName'); }
      if (ba.lastName && !sa.lastName)   { sa.lastName  = ba.lastName;      log('  billing->shipping: lastName'); }
      if (ba.address1 && !sa.address1)   { sa.address1  = ba.address1;      log('  billing->shipping: address1'); }
      if (ba.address2 && !sa.address2)   { sa.address2  = ba.address2;      log('  billing->shipping: address2'); }
      if (ba.city && !sa.city)           { sa.city      = ba.city;          log('  billing->shipping: city'); }
      if (ba.zip && !sa.zip)             { sa.zip       = ba.zip;           log('  billing->shipping: zip'); }
      if (ba.phone && !sa.phone)         { sa.phone     = ba.phone;         log('  billing->shipping: phone'); }
      if (ba.countryCode)                { sa.countryCode = ba.countryCode;  log('  billing->shipping: countryCode'); }
      if (ba.zoneCode && !sa.zoneCode)   { sa.zoneCode  = ba.zoneCode;      log('  billing->shipping: zoneCode'); }
    } else {
      log('billingSameAsShipping=false — billing and shipping remain independent');
    }

    // --- Safety net: guarantee a non-empty address on both blocks ---------
    //    The B2B backend requires a shipping (main) address and rejects an empty one
    //    with three "can't be blank" errors. If field mapping missed the (possibly
    //    localized) shipping fields, or the "same as billing" box was left unchecked on
    //    a single-address form, shipping ends up empty. When one block is complete and
    //    the other is not, mirror the complete one into the gaps so registration can
    //    proceed. Only empty fields are filled — real per-block data is never overwritten.
    const sa = payload.shippingAddress;
    const ba = payload.billingAddress;
    const addrComplete = (a) => !!(a.address1 && a.city && a.zip);

    if (!addrComplete(sa) && addrComplete(ba)) {
      warn('Shipping address incomplete but billing complete — mirroring billing -> shipping (fallback)');
      if (!sa.firstName)  sa.firstName   = ba.firstName || payload.firstName;
      if (!sa.lastName)   sa.lastName    = ba.lastName  || payload.lastName;
      if (!sa.address1)   sa.address1    = ba.address1;
      if (!sa.address2)   sa.address2    = ba.address2;
      if (!sa.city)       sa.city        = ba.city;
      if (!sa.zip)        sa.zip         = ba.zip;
      if (!sa.phone)      sa.phone       = ba.phone || payload.phone;
      if (ba.countryCode) sa.countryCode = ba.countryCode;
      if (!sa.zoneCode)   sa.zoneCode    = ba.zoneCode;
    } else if (!addrComplete(ba) && addrComplete(sa)) {
      warn('Billing address incomplete but shipping complete — mirroring shipping -> billing (fallback)');
      if (!ba.firstName)  ba.firstName   = sa.firstName || payload.firstName;
      if (!ba.lastName)   ba.lastName    = sa.lastName  || payload.lastName;
      if (!ba.address1)   ba.address1    = sa.address1;
      if (!ba.address2)   ba.address2    = sa.address2;
      if (!ba.city)       ba.city        = sa.city;
      if (!ba.zip)        ba.zip         = sa.zip;
      if (!ba.phone)      ba.phone       = sa.phone || payload.phone;
      if (sa.countryCode) ba.countryCode = sa.countryCode;
      if (!ba.zoneCode)   ba.zoneCode    = sa.zoneCode;
    }

    log('Final payload:', JSON.stringify(payload, null, 2));
    return payload;
  }

  // ── UI helpers ──────────────────────────────────────────────────────

  function showMessage(container, type, text) {
    const existing = container.querySelector('.b2b-register-msg');
    if (existing) existing.remove();

    const msg = document.createElement('div');
    msg.className = 'b2b-register-msg';
    msg.style.cssText =
      'padding:16px;margin:16px 0;border-radius:8px;text-align:center;font-size:15px;font-weight:500;';

    if (type === 'success') {
      msg.style.background = '#e8f5e9';
      msg.style.color = '#2e7d32';
      msg.style.border = '1px solid #a5d6a7';
    } else if (type === 'error') {
      msg.style.background = '#fce4ec';
      msg.style.color = '#c62828';
      msg.style.border = '1px solid #ef9a9a';
    } else {
      msg.style.background = '#e3f2fd';
      msg.style.color = '#1565c0';
      msg.style.border = '1px solid #90caf9';
    }

    msg.textContent = text;
    container.prepend(msg);
  }

  function setFormLoading(form, loading) {
    const submitBtn = form.querySelector('[type="submit"], button:not([type="button"])');
    const inputs = form.querySelectorAll('input, select, textarea, button');

    if (loading) {
      for (const el of inputs) el.disabled = true;
      if (submitBtn) {
        submitBtn.dataset.originalText = submitBtn.textContent;
        submitBtn.textContent = t('submitting');
      }
    } else {
      for (const el of inputs) el.disabled = false;
      if (submitBtn && submitBtn.dataset.originalText) {
        submitBtn.textContent = submitBtn.dataset.originalText;
      }
    }
  }

  // ── Analytics ───────────────────────────────────────────────────────
  //    The Shopify Forms submit button ("Schicken") sits in a shadow root,
  //    so a click trigger on its id never fires at document level. Emit
  //    named events instead. Shopify.analytics.publish reaches the sandboxed
  //    custom pixels (which have no DOM access at all) — they subscribe with
  //    analytics.subscribe('trena:b2b_register_submit', ...). window.dataLayer
  //    is kept as a fallback for a theme-level GTM container. Custom events are
  //    namespaced per Shopify's convention to avoid standard-event collisions.

  function trackEvent(name) {
    const payload = { form_id: 'b2b-register-form', locale: getLang() };

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, ...payload });
    } catch (e) {
      warn('dataLayer push failed:', e);
    }

    try {
      window.Shopify?.analytics?.publish?.(name, payload);
    } catch (e) {
      warn('Shopify.analytics.publish failed:', e);
    }

    log('Analytics event emitted:', name);
  }

  // ── Core submission handler ─────────────────────────────────────────

  function handleSubmit(form) {
    if (submitInProgress) {
      warn('Submit already in progress — ignoring duplicate');
      return;
    }
    submitInProgress = true;
    log('Submit handler fired — building payload...');
    trackEvent('trena:b2b_register_submit');

    const container = document.getElementById(CONTAINER_ID);
    const payload = buildPayload(form);

    // Basic validation
    if (!payload.companyName || !payload.vatNumber || !payload.email) {
      const missing = [];
      if (!payload.companyName) missing.push('companyName');
      if (!payload.vatNumber) missing.push('vatNumber');
      if (!payload.email) missing.push('email');
      warn('Validation FAILED — missing required fields:', missing.join(', '));
      showMessage(
        container,
        'error',
        t('requiredFields')
      );
      submitInProgress = false;
      return;
    }
    // Address validation. After buildPayload's safety-net mirroring, a user who filled
    // either address block has both populated; both blank means a genuine gap. We check
    // both blocks (not just billing) so an empty shipping address surfaces the friendly
    // localized message here instead of the backend's raw "can't be blank" errors.
    // The message lists only the fields actually missing (a field type counts as missing
    // if it's blank in either block), so filling 2 of 3 no longer reports all 3.
    const ADDR_FIELDS = [
      { key: 'address1', label: 'fieldAddress' },
      { key: 'city',     label: 'fieldCity' },
      { key: 'zip',      label: 'fieldZip' }
    ];
    const missingLabels = [];
    const missingDbg = [];
    for (const { key, label } of ADDR_FIELDS) {
      const blankBilling = !payload.billingAddress[key];
      const blankShipping = !payload.shippingAddress[key];
      if (blankBilling || blankShipping) {
        missingLabels.push(t(label));
        if (blankBilling) missingDbg.push('billingAddress.' + key);
        if (blankShipping) missingDbg.push('shippingAddress.' + key);
      }
    }
    if (missingLabels.length) {
      warn('Validation FAILED — missing address fields:', missingDbg.join(', '));
      showMessage(
        container,
        'error',
        `${t('addressLead')} ${missingLabels.join(', ')}.`
      );
      submitInProgress = false;
      return;
    }

    log('Validation PASSED');
    setFormLoading(form, true);
    showMessage(container, 'info', t('inProgress'));

    log('POST', ENDPOINT, '— payload size:', JSON.stringify(payload).length, 'bytes');

    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(res => {
        log('Response status:', res.status, res.statusText);
        if (!res.ok) {
          return res.json().then(data => {
            err('Server error response body:', JSON.stringify(data));
            if (data.violations && data.violations.length) {
              throw new Error(data.violations.map(v => v.message).join('\n'));
            }
            throw new Error(data.message || data.error || `${t('registrationError')} (${res.status})`);
          }).catch(parseErr => {
            if (parseErr.message && parseErr.message !== t('registrationError')) throw parseErr;
            err('Could not parse error response body');
            throw new Error(`${t('registrationError')} (${res.status})`);
          });
        }
        return res.json();
      })
      .then(data => {
        log('Registration SUCCESS — server response:', JSON.stringify(data));
        trackEvent('trena:b2b_register_success');
        showMessage(
          container,
          'success',
          data.message || t('success')
        );
        form.style.display = 'none';
        submitInProgress = false;
      })
      .catch(catchErr => {
        err('Registration FAILED:', catchErr.message || catchErr);
        trackEvent('trena:b2b_register_error');
        showMessage(container, 'error', catchErr.message || t('genericError'));
        setFormLoading(form, false);
        submitInProgress = false;
      });
  }

  // ── Form discovery (including shadow DOM) ───────────────────────────

  /**
   * Search for a <form> inside a root, traversing into open shadow roots.
   */
  function findForm(root) {
    const form = root.querySelector('form');
    if (form) return form;

    // Traverse shadow roots
    for (const el of root.querySelectorAll('*')) {
      if (el.shadowRoot) {
        log(`Traversing shadow root on <${el.tagName.toLowerCase()}>`);
        const found = findForm(el.shadowRoot);
        if (found) return found;
      }
    }
    return null;
  }

  // ── Interception layers ─────────────────────────────────────────────

  function interceptForm(form) {
    if (form.dataset.b2bIntercepted) {
      log('Form already intercepted — skipping');
      return;
    }
    form.dataset.b2bIntercepted = 'true';
    interceptActive = true;

    // Log a snapshot of all form fields for diagnostics
    const allInputs = form.querySelectorAll('input, select, textarea');
    log(`Form intercepted — field inventory (${allInputs.length} elements):`);
    for (const input of allInputs) {
      log(`  ${describeInput(input)}`);
    }

    // ── Layer 1: capture-phase submit listener on the form ───────────
    form.addEventListener('submit', (e) => {
      log('Layer 1 (form submit capture) — intercepted submit event');
      e.preventDefault();
      e.stopImmediatePropagation();
      handleSubmit(form);
    }, true);
    log('Layer 1 attached: capture-phase "submit" listener on <form>');

    // ── Analytics hooks ──────────────────────────────────────────────
    //    The form and its submit button are rendered by the Shopify Forms
    //    app, so we cannot give them an id in Liquid. Stamp stable ids here
    //    so GTM/GA4 can target them (b2b-register-form-submit = "Schicken").
    //    Shopify Forms renders inside an open shadow root, so clicks reaching
    //    document level are retargeted to the host element — tag the host too,
    //    and push a dataLayer event (the only reliable submit trigger).
    if (!form.id) form.id = 'b2b-register-form';
    const shadowHost = form.getRootNode()?.host;
    if (shadowHost && !shadowHost.id) shadowHost.id = 'b2b-register-form-embed';

    // ── Layer 2: capture-phase click listener on submit button ────────
    //    Fallback if Shopify Forms triggers fetch on button click
    //    without going through the form submit event.
    const submitBtn = form.querySelector('[type="submit"], button:not([type="button"])');
    if (submitBtn) {
      if (!submitBtn.id) submitBtn.id = 'b2b-register-form-submit';
      submitBtn.addEventListener('click', (e) => {
        // Only act if the submit event did NOT already fire.
        // Give a micro-task delay to let the submit event fire first.
        setTimeout(() => {
          if (!submitInProgress) {
            log('Layer 2 (button click fallback) — submit event did NOT fire, triggering from click');
            e.preventDefault();
            handleSubmit(form);
          }
        }, 0);
      }, true);
      log('Layer 2 attached: capture-phase "click" listener on submit button', describeInput(submitBtn));
    } else {
      warn('Layer 2: no submit button found — click fallback not attached');
    }

    // ── Layer 3: capture-phase submit on the container ────────────────
    //    Catches submit if it bubbles from a shadow-DOM form to the
    //    light-DOM container.
    const container = document.getElementById(CONTAINER_ID);
    if (container) {
      container.addEventListener('submit', (e) => {
        if (e.target === form) return; // already handled by Layer 1
        log('Layer 3 (container submit capture) — intercepted submit from different target:', e.target);
        e.preventDefault();
        e.stopImmediatePropagation();
        // Try to use the event target as the form
        const actualForm = e.target.closest ? e.target.closest('form') : form;
        handleSubmit(actualForm || form);
      }, true);
      log(`Layer 3 attached: capture-phase "submit" listener on #${CONTAINER_ID}`);
    }

    log('All interception layers active');
  }

  // ── Layer 4: fetch interceptor (safety net) ─────────────────────────
  //    Blocks Shopify Forms API calls once our interceptor is active.
  //    This catches cases where Shopify Forms bypasses the DOM submit
  //    event entirely and calls fetch() directly.

  function installFetchInterceptor() {
    const originalFetch = window.fetch;

    window.fetch = function (resource, options) {
      if (!interceptActive) {
        return originalFetch.apply(this, arguments);
      }

      const url = typeof resource === 'string' ? resource : (resource?.url ?? '');
      const method = (options?.method ?? 'GET').toUpperCase();

      // Detect Shopify Forms submission endpoints
      const isShopifyForms = method === 'POST' && (
        url.includes('/forms/') ||
        url.includes('forms.shopify') ||
        url.includes('monorail') ||
        url.includes('/form_submissions')
      );

      if (isShopifyForms) {
        warn('Layer 4 (fetch interceptor) — BLOCKED Shopify Forms request:', method, url);
        log('  This request was blocked because our B2B interceptor already handles form submission.');
        log('  If this is unexpected, check that the form submit event is firing correctly.');
        // Return a fake successful response so Shopify Forms doesn't show an error
        return Promise.resolve(new Response(JSON.stringify({ success: true }), {
          status: 200,
          headers: { 'Content-Type': 'application/json' }
        }));
      }

      // Allow our own submission and all other requests through
      return originalFetch.apply(this, arguments);
    };

    log('Layer 4 installed: fetch interceptor (blocks Shopify Forms API calls when intercept is active)');
  }

  // ── Layer 5: XMLHttpRequest interceptor (legacy safety net) ─────────
  //    Some older Shopify Forms versions may use XHR instead of fetch.

  function installXHRInterceptor() {
    const OriginalXHR = window.XMLHttpRequest;
    const originalOpen = OriginalXHR.prototype.open;
    const originalSend = OriginalXHR.prototype.send;

    OriginalXHR.prototype.open = function (method, url) {
      this._b2bMethod = method;
      this._b2bUrl = url;
      return originalOpen.apply(this, arguments);
    };

    OriginalXHR.prototype.send = function () {
      if (!interceptActive) {
        return originalSend.apply(this, arguments);
      }

      const url = this._b2bUrl || '';
      const method = (this._b2bMethod || 'GET').toUpperCase();

      const isShopifyForms = method === 'POST' && (
        url.includes('/forms/') ||
        url.includes('forms.shopify') ||
        url.includes('monorail') ||
        url.includes('/form_submissions')
      );

      if (isShopifyForms) {
        warn('Layer 5 (XHR interceptor) — BLOCKED Shopify Forms XHR:', method, url);
        // Simulate a successful response
        const self = this;
        setTimeout(() => {
          Object.defineProperty(self, 'status', { value: 200, writable: false });
          Object.defineProperty(self, 'responseText', { value: '{"success":true}', writable: false });
          Object.defineProperty(self, 'readyState', { value: 4, writable: false });
          if (typeof self.onreadystatechange === 'function') self.onreadystatechange();
          if (typeof self.onload === 'function') self.onload();
        }, 0);
        return;
      }

      return originalSend.apply(this, arguments);
    };

    log('Layer 5 installed: XMLHttpRequest interceptor');
  }

  // ── Initialisation ──────────────────────────────────────────────────

  function init() {
    log(`Init — looking for container #${CONTAINER_ID}`);
    const container = document.getElementById(CONTAINER_ID);

    if (!container) {
      warn(`Container #${CONTAINER_ID} NOT found in DOM — interceptor will not run`);
      return;
    }
    log(`Container #${CONTAINER_ID} found`);

    // Install network interceptors immediately (before form renders)
    installFetchInterceptor();
    installXHRInterceptor();

    // Try to find form immediately (including shadow DOM)
    const form = findForm(container);
    if (form) {
      log('Form found immediately — attaching interceptor');
      interceptForm(form);
      return;
    }

    log('Form not yet rendered — starting MutationObserver + polling fallback');

    // ── MutationObserver: reacts instantly when form is added to DOM ──
    let observer;
    let observerFired = false;

    function onFormFound(f, source) {
      if (observerFired) return;
      observerFired = true;
      log('Form discovered via', source);
      if (observer) observer.disconnect();
      if (pollInterval) clearInterval(pollInterval);
      interceptForm(f);
    }

    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(() => {
        const f = findForm(container);
        if (f) onFormFound(f, 'MutationObserver');
      });
      observer.observe(container, { childList: true, subtree: true });
      log(`MutationObserver watching #${CONTAINER_ID} (childList + subtree)`);
    } else {
      warn('MutationObserver not available — relying on polling only');
    }

    // ── Polling fallback (catches shadow-DOM additions the observer might miss) ──
    let polls = 0;
    const pollInterval = setInterval(() => {
      polls++;
      const f = findForm(container);
      if (f) {
        onFormFound(f, `polling (poll #${polls}, ${polls * POLL_INTERVAL}ms)`);
      } else if (polls >= MAX_POLLS) {
        err('Form NOT found after', MAX_POLLS, `polls (${MAX_POLLS * POLL_INTERVAL / 1000}s) — giving up`);
        clearInterval(pollInterval);
        if (observer) observer.disconnect();
      }
    }, POLL_INTERVAL);
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    log('DOM not ready — waiting for DOMContentLoaded');
    document.addEventListener('DOMContentLoaded', init);
  } else {
    log('DOM already ready — running init');
    init();
  }
})();
